import { getAPI } from "../../../api/Networking";
import { PaginatedAPIResponse } from "../../../models/NetworkResponse";
import { Carrier, ListCarriersParams } from "../../../models/Carriers/Carrier";

const CARRIERS_ADMIN_BASE = "/v1/carriers/admin";

function pickStr(value: unknown): string | null {
  if (value == null) return null;
  const text = typeof value === "string" ? value.trim() : String(value).trim();
  return text.length ? text : null;
}

function pickNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asBool(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

function normalizeCarrier(raw: unknown): Carrier {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    id: pickNum(row.id) ?? 0,
    code: pickStr(row.code) ?? "",
    name: pickStr(row.name) ?? "",
    tracking_url_template: pickStr(row.tracking_url_template),
    is_active: asBool(row.is_active),
    integration_type: pickStr(row.integration_type),
    delivery_partner_id: pickNum(row.delivery_partner_id),
    created_by: pickNum(row.created_by),
    updated_by: pickNum(row.updated_by),
    created_at: pickStr(row.created_at) ?? "",
    updated_at: pickStr(row.updated_at) ?? "",
  };
}

function assertSuccess(response: {
  success?: boolean;
  message?: string;
}): void {
  if (response && response.success === false) {
    throw new Error(response.message || "Failed to load carriers.");
  }
}

export default class CarriersAPI {
  listCarriers = async (
    params?: ListCarriersParams,
  ): Promise<PaginatedAPIResponse<Carrier>> => {
    try {
      const queryParams = new URLSearchParams();
      const search = params?.search?.trim();
      if (search) {
        queryParams.append("search", search);
      }
      if (params?.is_active !== undefined) {
        queryParams.append("is_active", String(params.is_active));
      }
      if (params?.page_no !== undefined) {
        queryParams.append("page_no", String(params.page_no));
      }
      if (params?.limit !== undefined) {
        queryParams.append("limit", String(params.limit));
      }

      const query = queryParams.toString();
      const response = await getAPI(
        query ? `${CARRIERS_ADMIN_BASE}?${query}` : CARRIERS_ADMIN_BASE,
      );
      assertSuccess(response);

      const result = (response.result ?? {}) as Record<string, unknown>;
      const rows = Array.isArray(result.list) ? result.list : [];

      return {
        page_no: pickNum(result.page_no) ?? 1,
        total_pages: pickNum(result.total_pages) ?? 0,
        total_count: pickNum(result.total_count) ?? 0,
        limit: pickNum(result.limit) ?? params?.limit ?? 20,
        list: rows.map(normalizeCarrier),
      };
    } catch (error) {
      throw error;
    }
  };
}
